import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import { createTheme as createThemeV5, ThemeProvider as ThemeProviderV5, StyledEngineProvider } from "@mui/material/styles";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          typography: {
            fontFamily: [
              '"Inter"',
              '"Roboto"',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif'
            ].join(','),
            
            // Configurações para diferentes elementos
            h1: {
              fontSize: '2.125rem',
              fontWeight: 600,
              letterSpacing: '-0.01562em'
            },
            h2: {
              fontSize: '1.5rem',
              fontWeight: 600,
              letterSpacing: '-0.00833em'
            },
            h3: {
              fontSize: '1.25rem',
              fontWeight: 500,
              letterSpacing: '0em'
            },
            h4: {
              fontSize: '1.125rem',
              fontWeight: 500,
              letterSpacing: '0.00735em'
            },
            h5: {
              fontSize: '1rem',
              fontWeight: 500,
              letterSpacing: '0em'
            },
            h6: {
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.0075em'
            },
            body1: {
              fontSize: '1rem',
              fontWeight: 400,
              letterSpacing: '0.00938em',
              lineHeight: 1.5
            },
            body2: {
              fontSize: '0.875rem',
              fontWeight: 400,
              letterSpacing: '0.01071em',
              lineHeight: 1.43
            },
            button: {
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.02857em',
              textTransform: 'none' // Remove o uppercase padrão dos botões
            },
            caption: {
              fontSize: '0.75rem',
              fontWeight: 400,
              letterSpacing: '0.03333em'
            },
            overline: {
              fontSize: '0.625rem',
              fontWeight: 400,
              letterSpacing: '0.08333em',
              textTransform: 'uppercase'
            }
          },
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : "hsl(270, 80%, 60%)",
              borderRadius: "4px",
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "hsl(270, 15%, 15%)",
              borderRadius: "4px",
            },
          },
          palette: {
            type: mode,
            primary: { main: mode === "light" ? primaryColorLight : "hsl(270, 80%, 60%)" },
            secondary: { main: mode === "light" ? "#FF6B6B" : "hsl(270, 20%, 15%)" },
            accent: { main: "hsl(25, 95%, 55%)" }, // Novo token de destaque (laranja)
            textPrimary: mode === "light" ? primaryColorLight : "hsl(0, 0%, 95%)",
            borderPrimary: mode === "light" ? primaryColorLight : "hsl(270, 15%, 18%)",
            dark: { main: mode === "light" ? "#333333" : "hsl(0, 0%, 95%)" },
            light: { main: mode === "light" ? "#F3F3F3" : "hsl(270, 30%, 6%)" },
            fontColor: mode === "light" ? primaryColorLight : "hsl(0, 0%, 95%)",
            tabHeaderBackground: mode === "light" ? "#EEE" : "hsl(270, 25%, 10%)",
            optionsBackground: mode === "light" ? "#fafafa" : "hsl(270, 15%, 15%)",
            fancyBackground: mode === "light" ? "#fafafa" : "hsl(270, 30%, 6%)",
            total: mode === "light" ? "#fff" : "hsl(270, 25%, 10%)",
            messageIcons: mode === "light" ? "grey" : "hsl(0, 0%, 95%)",
            inputBackground: mode === "light" ? "#FFFFFF" : "hsl(270, 15%, 18%)",
            barraSuperior: mode === "light" ? primaryColorLight : "hsl(270, 30%, 6%)",
            divider: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "hsl(270, 15%, 18%)",
            background: {
              default: mode === "light" ? "#fafafa" : "hsl(270, 30%, 6%)",
              paper: mode === "light" ? "#ffffff" : "hsl(270, 25%, 10%)",
            },
            text: {
              primary: mode === "light" ? "#000000" : "hsl(0, 0%, 95%)",
              secondary: mode === "light" ? "#666666" : "hsl(270, 10%, 60%)",
            },
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  const themeV5 = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: mode,
          primary: { main: mode === "light" ? primaryColorLight : "hsl(270, 80%, 60%)" },
          secondary: { main: mode === "light" ? "#FF6B6B" : "hsl(270, 20%, 15%)" },
          background: {
            default: mode === "light" ? "#fafafa" : "hsl(270, 30%, 6%)",
            paper: mode === "light" ? "#ffffff" : "hsl(270, 25%, 10%)",
          },
          text: {
            primary: mode === "light" ? "#000000" : "hsl(0, 0%, 95%)",
            secondary: mode === "light" ? "#666666" : "hsl(270, 10%, 60%)",
          },
          divider: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "hsl(270, 15%, 18%)",
        },
        typography: {
          fontFamily: [
            '"Inter"',
            '"Roboto"',
            '"Segoe UI"',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif'
          ].join(','),
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                textTransform: 'none',
              },
              containedPrimary: {
                background: mode === 'dark' ? 'linear-gradient(135deg, hsl(270, 80%, 60%) 0%, hsl(280, 80%, 60%) 100%)' : undefined,
                boxShadow: mode === 'dark' ? '0 4px 14px 0 hsla(270, 80%, 60%, 0.39)' : undefined,
                '&:hover': {
                  background: mode === 'dark' ? 'linear-gradient(135deg, hsl(270, 80%, 55%) 0%, hsl(280, 80%, 55%) 100%)' : undefined,
                }
              }
            }
          }
        }
      }),
    [mode, primaryColorLight, primaryColorDark]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    console.log("|=========== handleSaveSetting ==========|")
    console.log("APP START")
    console.log("|========================================|")
   
    
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(file ? getBackendUrl() + "/public/" + file : defaultLogoLight);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(file ? getBackendUrl() + "/public/" + file : defaultLogoDark);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Chat-flow");
      })
      .catch((error) => {
        console.log("!==== Erro ao carregar temas: ====!", error);
        setAppName("chat-flow");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : "hsl(270, 80%, 60%)");
    
    if (mode === "dark") {
      root.style.setProperty("--background", "270 30% 6%");
      root.style.setProperty("--foreground", "0 0% 95%");
      root.style.setProperty("--card", "270 25% 10%");
      root.style.setProperty("--card-foreground", "0 0% 95%");
      root.style.setProperty("--popover", "270 25% 10%");
      root.style.setProperty("--popover-foreground", "0 0% 95%");
      root.style.setProperty("--primary", "270 80% 60%");
      root.style.setProperty("--primary-foreground", "0 0% 100%");
      root.style.setProperty("--secondary", "270 20% 15%");
      root.style.setProperty("--secondary-foreground", "0 0% 90%");
      root.style.setProperty("--muted", "270 15% 15%");
      root.style.setProperty("--muted-foreground", "270 10% 60%");
      root.style.setProperty("--accent", "25 95% 55%");
      root.style.setProperty("--accent-foreground", "0 0% 100%");
      root.style.setProperty("--border", "270 15% 18%");
      root.style.setProperty("--input", "270 15% 18%");
      root.style.setProperty("--ring", "270 80% 60%");
    } else {
      // Opcional: Definir variáveis de fallback para o modo claro se necessário
      root.style.removeProperty("--background");
      root.style.removeProperty("--foreground");
      root.style.removeProperty("--card");
      root.style.removeProperty("--card-foreground");
      root.style.removeProperty("--popover");
      root.style.removeProperty("--popover-foreground");
      root.style.removeProperty("--primary");
      root.style.removeProperty("--primary-foreground");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--secondary-foreground");
      root.style.removeProperty("--muted");
      root.style.removeProperty("--muted-foreground");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--accent-foreground");
      root.style.removeProperty("--border");
      root.style.removeProperty("--input");
      root.style.removeProperty("--ring");
    }
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon ? getBackendUrl() + "/public/" + appLogoFavicon : defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <StyledEngineProvider injectFirst>
          <ThemeProviderV5 theme={themeV5}>
            <ThemeProvider theme={theme}>
              <QueryClientProvider client={queryClient}>
                <ActiveMenuProvider>
                  <Routes />
                </ActiveMenuProvider>
              </QueryClientProvider>
            </ThemeProvider>
          </ThemeProviderV5>
        </StyledEngineProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
